import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

function DetailItem({ icon, label, value, fullWidth = false }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Box
        sx={{
          height: "100%",
          p: 1.75,
          borderRadius: 2,
          border: "1px solid #ede9fe",
          bgcolor: "#faf9ff",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: "#f3e8ff",
              color: "#7c3aed",
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                mb: 0.25,
                color: "#64748b",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".045em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
            <Typography
              title={value || "—"}
              sx={{
                color: "#1e293b",
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1.5,
                overflowWrap: "anywhere",
              }}
            >
              {value || "—"}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Grid>
  );
}

function StatusChip({ status }) {
  if (status === "conformed") {
    return (
      <Chip
        icon={<CheckCircleOutlineIcon />}
        label="Confirmed"
        size="small"
        sx={{
          bgcolor: "#ecfdf5",
          color: "#047857",
          border: "1px solid #a7f3d0",
          fontWeight: 700,
          "& .MuiChip-icon": { color: "#059669" },
        }}
      />
    );
  }
  if (status === "rejected") {
    return (
      <Chip
        icon={<CancelOutlinedIcon />}
        label="Rejected"
        size="small"
        sx={{
          bgcolor: "#fef2f2",
          color: "#b91c1c",
          border: "1px solid #fecaca",
          fontWeight: 700,
          "& .MuiChip-icon": { color: "#dc2626" },
        }}
      />
    );
  }
  return (
    <Chip
      label="Awaiting reply"
      size="small"
      sx={{
        bgcolor: "#fffbeb",
        color: "#b45309",
        border: "1px solid #fde68a",
        fontWeight: 700,
      }}
    />
  );
}

export default function SessionDetailsDialog({
  open,
  session,
  loadingStatus,
  onClose,
  onConfirm,
  onReject,
}) {
  if (!session) return null;

  const updating = Boolean(loadingStatus);

  return (
    <Dialog
      open={open}
      onClose={updating ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(124,58,237,.14)",
          boxShadow: "0 28px 80px rgba(30,41,59,.24)",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(15,23,42,.55)",
          backdropFilter: "blur(5px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          background:
            "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 52%, #ffffff 100%)",
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Avatar
              sx={{
                width: 46,
                height: 46,
                bgcolor: "#7c3aed",
                backgroundImage:
                  "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                boxShadow: "0 7px 18px rgba(124,58,237,.25)",
              }}
            >
              <PersonOutlineIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: { xs: 18, sm: 20 },
                  fontWeight: 800,
                  letterSpacing: "-.02em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {session.name || "Session details"}
              </Typography>
              <Typography sx={{ mt: 0.25, color: "#64748b", fontSize: 12.5 }}>
                Booking and customer information
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={updating}
              aria-label="Close session details"
              size="small"
              sx={{
                color: "#64748b",
                bgcolor: "rgba(255,255,255,.75)",
                border: "1px solid #e2e8f0",
                "&:hover": { bgcolor: "#fff", color: "#7c3aed" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            <StatusChip status={session.status} />
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
        <Grid container spacing={1.5}>
          <DetailItem
            icon={<MailOutlineIcon />}
            label="Email"
            value={session.email}
          />
          <DetailItem
            icon={<PhoneOutlinedIcon />}
            label="Mobile"
            value={session.mobileNumber}
          />
          <DetailItem
            icon={<BusinessOutlinedIcon />}
            label="Company"
            value={session.companyName}
          />
          <DetailItem
            icon={<EventOutlinedIcon />}
            label="Booking date"
            value={formatDate(session.bookedDate)}
          />
          <DetailItem
            icon={<AccessTimeOutlinedIcon />}
            label="Received date"
            value={formatDate(session.createdAt)}
          />
          <DetailItem
            icon={<LocationOnOutlinedIcon />}
            label="Address"
            value={session.address}
            fullWidth
          />
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          justifyContent: "flex-end",
          bgcolor: "#fcfcff",
        }}
      >
        <Button
          onClick={onClose}
          disabled={updating}
          sx={{
            color: "#64748b",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
          }}
        >
          Close
        </Button>

        {session.status === "pending" && (
          <>
            <Button
              onClick={() => onReject(session._id)}
              disabled={updating}
              startIcon={
                loadingStatus === "rejecting" ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <CancelOutlinedIcon />
                )
              }
              sx={{
                color: "#b91c1c",
                bgcolor: "#fee2e2",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
                "&:hover": { bgcolor: "#fecaca" },
              }}
            >
              Reject
            </Button>
            <Button
              onClick={() => onConfirm(session._id)}
              disabled={updating}
              variant="contained"
              startIcon={
                loadingStatus === "confirming" ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
              sx={{
                color: "#fff",
                backgroundImage:
                  "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
                boxShadow: "0 5px 15px rgba(99,102,241,.22)",
                "&:hover": {
                  backgroundImage:
                    "linear-gradient(to right, #2563eb 0%, #db2777 100%)",
                  boxShadow: "0 7px 18px rgba(99,102,241,.28)",
                },
              }}
            >
              Confirm
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
